import { useCallback } from "react";
import { useUpdateExperimentMutation } from "src/api/services/experimentsApi";
import { AbTest, InputType } from "src/api/types";
import ProductsSelect from "src/components/ProductsSelect";
import { createDrawerProvider } from "src/utils/createDrawerProvider";

const { DrawerProvider, useDrawerContext } =
  createDrawerProvider<AbTest>("Update Experiment");

const ExperimentsDrawerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [updateExperiment, { isLoading }] = useUpdateExperimentMutation();

  const onSubmit = useCallback(
    (item: AbTest) => {
      updateExperiment({
        id: item.id,
        name: item.name,
        description: item.description,
        product_ids: item.products.map((p) => p.id),
      });
    },
    [updateExperiment],
  );

  const getInputs = useCallback(
    (
      item: AbTest,
      setItem: (key: keyof AbTest, value: AbTest[keyof AbTest]) => void,
    ): InputType<AbTest>[] => [
      {
        type: "text",
        name: "name",
        label: "Name",
        required: true,
        value: item.name,
        onChange: (v) => setItem("name", v),
      },
      {
        type: "text",
        multiline: true,
        minRows: 3,
        name: "description",
        label: "Description",
        required: false,
        value: item.description ?? "",
        onChange: (v) => setItem("description", v),
      },
      {
        type: "custom",
        label: "Products",
        component: () => (
          <ProductsSelect
            select={(p) => ({ id: p.id, name: p.name })}
            multiple={true}
            value={item.products}
            onChange={(v) => setItem("products", v)}
            required
          />
        ),
      },
    ],
    [],
  );

  return (
    <DrawerProvider
      getInputs={getInputs}
      loading={isLoading}
      onSubmit={onSubmit}
    >
      {children}
    </DrawerProvider>
  );
};

export default ExperimentsDrawerProvider;
export { useDrawerContext };
